<template>
  <div class="a11y-modal">
    <div class="title">
      <h2>Edit Credits</h2>
      <button class="close" @click="closeModal">×</button>
    </div>

    <form id="assignCredits" novalidate @submit.prevent="submit">
      <div class="content">
        <div class="row">
          <div class="col-12 mb-2">
            <ul class="dashboard-list">
              <li class="student">
                <div class="avatar hide-sm-down">
                  <div class="image">
                    <div
                      v-if="student.photoUrl"
                      class="user-img"
                      :style="`background-image: url(${student.photoUrl});`"
                    ></div>
                  </div>
                </div>
                <div>
                  <div class="primary">{{ student.fullName }}</div>
                  <div class="secondary">{{ student.email }}</div>
                </div>
              </li>
            </ul>
          </div>
          <div class="col-6">
            <form-component>
              <input-text
                id="individualCredits"
                v-model="individualCredits"
                v-validate="'required|integer|min_value:0'"
                data-vv-as="credits"
                :error="errors.first('individualCredits')"
                name="individualCredits"
                label="Individual Credits"
                type="number"
                :min="0"
                required
              />
            </form-component>
          </div>
          <div class="col-6">
            <form-component>
              <input-text
                id="groupCredits"
                v-model="groupCredits"
                v-validate="'required|integer|min_value:0'"
                data-vv-as="credits"
                :error="errors.first('groupCredits')"
                name="groupCredits"
                label="Wellbeing Workshop Credits"
                type="number"
                :min="0"
                required
              />
            </form-component>
          </div>
        </div>
      </div>
    </form>

    <div class="actions">
      <button type="button" @click="closeModal">Cancel</button>
      <button
        class="primary"
        type="button"
        :disabled="submitting || !amountUpdated"
        @click="submit"
      >
        <font-awesome-icon
          v-if="submitting"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Confirm</span>
      </button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import AdminClient from '@/api/AdminClient';
import FormComponent from '@/components/FormComponent.vue';
import InputText from '@/components/inputs/InputText.vue';

export default Vue.extend({
  name: 'EditStudentCreditsModal',

  components: {
    FormComponent,
    InputText
  },

  props: {
    schoolId: {
      type: Number,
      required: true
    },
    student:{
      type: Object,
      required: true
    }
  },

  data() {
    return {
      adminClient: new AdminClient(this.$http),
      submitting: false,

      individualCredits: this.student.individualCreditsRemaining.toString(),
      groupCredits: this.student.groupCreditsRemaining.toString(),
      amountUpdated: false,
    };
  },

  computed: {
    dirty() {
      return Object.keys(this.veeValidateFields).some((key) => this.veeValidateFields[key].dirty);
    }
  },
  watch: {
    individualCredits() {
      this.amountUpdated = true;
    },
    groupCredits() {
      this.amountUpdated = true;
    }
  },
  methods: {
    closeModal() {
      this.$modal.close();
    },

    async submit() {
      const valid = await this.$validator.validateAll();
      if (!valid) {
        return;
      }

      this.submitting = true;
      try {
        const indCredits = parseInt(this.individualCredits, 10);
        const grpCredits = parseInt(this.groupCredits, 10);
        await this.adminClient.setStudentCreditAllowance(
          this.schoolId,
          this.student.id,
          indCredits,
          grpCredits
        );

        this.updateStudentCredits(indCredits, grpCredits);

        this.$store.commit('setGlobalAlert', {
          msg: "Successfully updated the user's available credits!"
        });
      } catch (e) {
        this.$store.commit('setGlobalAlert', {
          msg: "There was an error updating the user's credits. Please try again!",
          error: true
        });
      }
      this.closeModal();
      this.submitting = false;
    },
    updateStudentCredits(individualRemaining, groupRemaining) {
      this.student.individualCreditAllowance = individualRemaining.toString();
      this.student.groupCreditAllowance = groupRemaining.toString();

      this.student.individualCreditsRemaining = individualRemaining.toString();
      this.student.groupCreditsRemaining = groupRemaining.toString();

      this.student.individualCreditAllowance = this.calcCreditsAllowanceString(
        individualRemaining,
        this.student.individualCreditsUsed);

      this.student.groupCreditAllowance = this.calcCreditsAllowanceString(
        groupRemaining,
        this.student.groupCreditsUsed);
    },
    calcCreditsAllowanceString(remaining, usedString) {
        const used = parseInt(usedString, 10)
        return remaining + used;
    }
  }
});
</script>

<style lang="scss" scoped>
.a11y-modal {
  ::v-deep .form .form__input {
    label {
      text-transform: none;
      font-size: 15px;
      letter-spacing: 0;
      margin-bottom: 0.5rem;
      font-weight: 500;
    }
    input {
      border: solid 1px #d1d1d1;
      background: #fff;
      color: inherit;
      margin-bottom: 0;

      &:focus {
        color: inherit;
        border: solid 1px $dark-blue;
        background: #f8f8f8;
      }

      &::placeholder {
        color: inherit;
      }
    }
  }
}

.student {
  padding-right: 0;
  padding-left: 0;
  justify-content: flex-start;
}
</style>
